/**
 * 骨架屏组件
 * 根据不同的布局模式展示对应的加载占位符
 * 完全匹配实际的 item 展示形式
 */

import { Skeleton } from '@/shared/ui/skeleton'

interface ItemSkeletonProps {
  mode: string
  count?: number  // 显示数量
}

// 卡片模式骨架屏 - 匹配 ItemCard
function CardSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[20px] bg-card shadow-[0_8px_24px_rgba(0,0,0,0.03)]">
      {/* 顶部图片区域 - 固定高度，避免被内容挤压 */}
      <Skeleton data-slot="card-skeleton-cover" className="h-[120px] w-full shrink-0 bg-[#eceef2] dark:bg-[#202124]" />
      
      {/* 内容区域 - 匹配 p-4 */}
      <div className="flex flex-1 flex-col p-4">
        {/* 标题行 */}
        <div className="flex justify-between items-start gap-2 mb-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
        
        {/* 描述 - 2行 */}
        <div className="space-y-1.5 mb-3">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
        
        {/* 标签区域 */}
        <div className="flex gap-1.5 mb-3 flex-1">
          <Skeleton className="h-5 w-16 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-md" />
        </div>
        
        {/* 底部：文件夹 + 日期 */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-4">
          <div className="flex items-center gap-1.5">
            <Skeleton className="h-3 w-3 rounded" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  )
}

// 列表模式骨架屏 - 匹配 ItemRow
function ListSkeleton() {
  return (
    <div className="relative flex items-start gap-3 rounded-lg px-3 py-4">
      {/* 左侧 Avatar/Logo */}
      <div className="pt-0.5 hidden sm:block">
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
      
      {/* 中间内容区域 */}
      <div className="min-w-0 flex-1 space-y-2">
        {/* 来源与时间 */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-12" />
        </div>
        {/* 标题 */}
        <Skeleton className="h-4 w-1/2" />
        {/* 描述 */}
        <Skeleton className="h-3 w-4/5" />
        {/* 文件夹与标签 */}
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-5 w-16 rounded-sm" />
          <Skeleton className="h-3 w-14" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-12" />
        </div>
      </div>
      
      {/* 右侧缩略图 */}
      <Skeleton className="ml-3 hidden h-[72px] w-[104px] shrink-0 rounded-lg md:block" />
    </div>
  )
}

// 极简模式骨架屏 - 匹配 ItemHeadline
function HeadlineSkeleton() {
  return (
    <div className="flex items-center gap-2 rounded-md px-3 py-2">
      {/* 类型图标 */}
      <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
      
      {/* 标题 */}
      <Skeleton className="h-4 w-1/3" />

      {/* 来源与文件夹 */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-12" />
      </div>

      <div className="flex-1" />

      {/* 右侧时间 */}
      <Skeleton className="h-3 w-12 shrink-0" />
    </div>
  )
}

// 瀑布流模式骨架屏 - 匹配瀑布流卡片
function WaterfallSkeleton() {
  return (
    <div className="break-inside-avoid mb-4 rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      {/* 图片区域 - 随机高度模拟瀑布流 */}
      <Skeleton className="w-full rounded-none" style={{ height: `${Math.random() * 100 + 150}px` }} />
      
      {/* 内容区域 */}
      <div className="p-3 space-y-2">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
        
        {/* 标签 */}
        <div className="flex gap-1.5 pt-1">
          <Skeleton className="h-5 w-14 rounded-md" />
          <Skeleton className="h-5 w-16 rounded-md" />
        </div>
        
        {/* 底部信息 */}
        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  )
}

export function ItemSkeleton({ mode, count = 12 }: ItemSkeletonProps) {
  // 根据模式选择对应的骨架屏组件
  const SkeletonComponent = {
    card: CardSkeleton,
    list: ListSkeleton,
    headline: HeadlineSkeleton,
    waterfall: WaterfallSkeleton
  }[mode] || CardSkeleton

  // 根据模式应用对应的容器样式 - 完全匹配 items-container.tsx 中的样式
  const containerClass = {
    // 匹配卡片布局；窄窗口尺寸由 workspace-density.css 与真实卡片同步调整
    card: 'grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4 auto-rows-[320px]',
    // 匹配列表布局
    list: 'space-y-1',
    // 匹配极简布局
    headline: 'space-y-1',
    // 匹配瀑布流布局
    waterfall: 'columns-[260px] gap-4'
  }[mode] || 'grid grid-cols-[repeat(auto-fill,minmax(min(240px,100%),1fr))] gap-4 auto-rows-[320px]'

  return (
    <div data-layout-mode={mode} className={`${containerClass} py-4`}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonComponent key={index} />
      ))}
    </div>
  )
}
